import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  Modal,
  Pressable,
} from "react-native";
import { ChevronDown } from "lucide-react";
import { TConsultView } from "../../../../../utils/types";
import { DummyDataToggle } from "./DummyDataToggle";
import { TipsToggleButton } from "./TipsToggleButton";
import styles from "../HomeConsults.styles";

type TConsultTopLevelView = "Appointments" | "Notes" | "Settings";
const TOP_LEVEL_VIEWS: TConsultTopLevelView[] = [
  "Appointments",
  "Notes",
  "Settings",
];

interface ConsultHeaderProps {
  currentView: TConsultView;
  selectedPatient: any;
  useDummyData: boolean;
  tipsEnabled: boolean;
  localTipsToggle: boolean;
  tourActive: boolean;
  onBack: () => void;
  onToggleDummyData: () => void;
  onToggleTips: () => void;
  selectedTopLevelView: TConsultTopLevelView;
  onTopLevelViewChange: (view: TConsultTopLevelView) => void;
  setShowConsultTips?: (show: boolean) => void;
}

export const ConsultHeader: React.FC<ConsultHeaderProps> = ({
  currentView,
  selectedPatient,
  useDummyData,
  tipsEnabled,
  localTipsToggle,
  tourActive,
  onBack,
  onToggleDummyData,
  onToggleTips,
  selectedTopLevelView,
  onTopLevelViewChange,
  setShowConsultTips,
}) => {
  const { width } = useWindowDimensions();
  const isNarrowScreen = width <= 480;
  const isTopLevelView =
    currentView === "baseView" ||
    currentView === "consultSettings" ||
    currentView === "consultNotes";
  const [showViewMenu, setShowViewMenu] = React.useState(false);
  const viewSelectorButtonRef = React.useRef<TouchableOpacity | null>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = React.useState({
    top: 0,
    left: 0,
    width: 168,
  });

  const handleViewSelect = (view: TConsultTopLevelView) => {
    onTopLevelViewChange(view);
    setShowViewMenu(false);
  };

  const handleOpenViewMenu = () => {
    if (!viewSelectorButtonRef.current) {
      setShowViewMenu(true);
      return;
    }

    viewSelectorButtonRef.current.measureInWindow(
      (x, y, buttonWidth, height) => {
        const menuWidth = Math.max(168, buttonWidth);
        const clampedLeft = Math.max(16, Math.min(x, width - menuWidth - 16));
        setViewMenuAnchor({
          top: y + height + 8,
          left: clampedLeft,
          width: menuWidth,
        });
        setShowViewMenu(true);
      },
    );
  };

  const renderViewSelector = () => (
    <>
      <TouchableOpacity
        ref={viewSelectorButtonRef}
        style={styles.viewSelectorButton}
        onPress={handleOpenViewMenu}
        accessibilityRole="button"
        accessibilityLabel="Select consult view"
      >
        <Text style={styles.viewSelectorButtonText}>
          {selectedTopLevelView}
        </Text>
        <ChevronDown size={16} color="#1F2937" />
      </TouchableOpacity>

      <Modal
        transparent
        visible={showViewMenu}
        animationType="fade"
        onRequestClose={() => setShowViewMenu(false)}
      >
        <Pressable
          style={styles.viewSelectorModalBackdrop}
          onPress={() => setShowViewMenu(false)}
        >
          <View
            style={[
              styles.viewSelectorMenu,
              {
                top: viewMenuAnchor.top,
                left: viewMenuAnchor.left,
                width: viewMenuAnchor.width,
              },
            ]}
          >
            {TOP_LEVEL_VIEWS.map((viewOption) => {
              const isSelected = selectedTopLevelView === viewOption;
              return (
                <TouchableOpacity
                  key={viewOption}
                  style={[
                    styles.viewSelectorMenuItem,
                    isSelected && styles.viewSelectorMenuItemSelected,
                  ]}
                  onPress={() => handleViewSelect(viewOption)}
                  accessibilityRole="button"
                  accessibilityLabel={`Switch to ${viewOption} view`}
                >
                  <Text
                    style={[
                      styles.viewSelectorMenuItemText,
                      isSelected && styles.viewSelectorMenuItemTextSelected,
                    ]}
                  >
                    {viewOption}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </>
  );

  const renderTopLevelHeader = () => (
    <View
      style={{
        flexDirection: isNarrowScreen ? "column" : "row",
        alignItems: isNarrowScreen ? "flex-start" : "center",
        justifyContent: "space-between",
        flex: 1,
        width: "100%",
        minWidth: 0,
        rowGap: isNarrowScreen ? 8 : 0,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", minWidth: 0 }}>
        <Text style={styles.headerTitle}>Consults</Text>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {currentView === "baseView" && (
          <DummyDataToggle
            useDummyData={useDummyData}
            onToggle={onToggleDummyData}
          />
        )}
        {currentView === "baseView" && (
          <TipsToggleButton tipsEnabled={tipsEnabled} onToggle={onToggleTips} />
        )}
        {renderViewSelector()}
      </View>
    </View>
  );

  const renderDetailViewHeader = () => (
    <View
      style={{
        flexDirection: isNarrowScreen ? "column" : "row",
        alignItems: isNarrowScreen ? "flex-start" : "center",
        justifyContent: "space-between",
        flex: 1,
        width: "100%",
        minWidth: 0,
        rowGap: isNarrowScreen ? 8 : 0,
      }}
    >
      <View style={{ flexDirection: "row", flexWrap: "wrap", minWidth: 0 }}>
        <TouchableOpacity onPress={onBack}>
          <Text style={[styles.headerTitle, styles.headerLink]}>
            Consults /{" "}
          </Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, styles.headerBold]}>
          {selectedPatient ? selectedPatient.name : ""}
        </Text>
        {currentView === "presentationView" && (
          <Text style={styles.headerTitle}> / Gallery</Text>
        )}
      </View>
      {selectedPatient && (
        <View style={styles.headerRightControls}>{renderViewSelector()}</View>
      )}
    </View>
  );

  return (
    <View style={styles.header}>
      <View style={styles.headerTitleContainer}>
        {isTopLevelView ? renderTopLevelHeader() : renderDetailViewHeader()}
      </View>
    </View>
  );
};
